import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { DateTime } from 'luxon';
import Button from '../../../../../../shared/design-system/components/atoms/button';
import Icon from '../../../../../../shared/design-system/components/atoms/icon/icon';
import ProspectsFilter from '../../../../../../shared/design-system/components/molecules/prospects-filter';
import { FilterType } from '../../../../../../shared/design-system/components/molecules/prospects-filter/type';
import {
  callerHostOptions,
  getCallOutcomeOptionsFromLogs,
} from '../../utils/helpers';
import { CallSourceActionType } from '../../../../types/call-logs';
import { CallLogsFiltersProps, CallLogsFiltersState } from './type';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';

const TABS_WITHOUT_SOURCE_AND_OUTCOME = ['missed', 'voicemails'];

const CallLogsFilters: React.FC<CallLogsFiltersProps> = ({
  show,
  onClose,
  initialFilters,
  onApply,
  onClearAll,
  callOutcomeList,
  sequencesList,
  sendGetCallOutcomeListRequest,
  sendGetTeamsAndMembersListRequest,
  sendGetProspectFiltersSequencesListRequest,
  teamsAndUsersList,
  activeTab,
}) => {
  const hideSourceAndOutcome = TABS_WITHOUT_SOURCE_AND_OUTCOME.includes(
    activeTab ?? '',
  );

  const [filters, setFilters] = useState<CallLogsFiltersState>(initialFilters);

  // Enum options first (backend receives name), then sequences (backend receives id, UI shows name)
  const sourceOptions = useMemo(() => {
    const enumOptions = Object.values(CallSourceActionType)?.map((val) => ({
      id: val,
      name: val,
    }));
    const sequenceOptions = (sequencesList ?? [])?.map((s) => ({
      id: s?.value,
      name: s?.name,
    }));
    return [...enumOptions, ...sequenceOptions];
  }, [sequencesList]);

  const callOutcomeOptions = useMemo(
    () => getCallOutcomeOptionsFromLogs(callOutcomeList),
    [callOutcomeList],
  );

  const callerHostOpts = useMemo(
    () => callerHostOptions(teamsAndUsersList) ?? [],
    [teamsAndUsersList],
  );

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters, show]);

  const callDateValues = useMemo(() => {
    if (!filters.callDateStart && !filters.callDateEnd) return [];
    return [
      { date: filters.callDateStart || '' },
      { date: filters.callDateEnd || '' },
    ];
  }, [filters.callDateStart, filters.callDateEnd]);

  const setCallDateFilter = (f: any[]) => {
    const startDate = f[0];
    const endDate = f[1];

    setFilters((prev) => ({
      ...prev,
      callDateStart: f[0]
        ? DateTime.fromJSDate(startDate).startOf('day').toString()
        : undefined,
      callDateEnd: f[1]
        ? DateTime.fromJSDate(endDate).endOf('day').toString()
        : undefined,
    }));
  };

  const setCallerHostFilter = useCallback(
    (selected: { id: string | number }[]) => {
      const ids = (selected ?? [])
        .map((s) => Number(s?.id))
        .filter((n) => !Number.isNaN(n));
      setFilters((prev) => ({
        ...prev,
        callerHost: ids,
      }));
    },
    [],
  );

  const sourceSelectedIds = useMemo(
    () => (filters.source ? filters.source : []),
    [filters.source],
  );

  const sourceValues = useMemo(
    () => sourceOptions?.filter((o) => sourceSelectedIds?.includes(o?.id)),
    [sourceOptions, sourceSelectedIds],
  );

  const callerHostSelectedIds = useMemo(
    () => (filters.callerHost ? filters.callerHost : []),
    [filters.callerHost],
  );

  const callerHostValues = useMemo(
    () =>
      callerHostOpts.filter((o) =>
        callerHostSelectedIds?.includes(Number(o?.id)),
      ),
    [callerHostOpts, callerHostSelectedIds],
  );

  const callOutcomeKeys = useMemo(
    () => (filters.callOutcome ? filters.callOutcome : []),
    [filters.callOutcome],
  );

  const callOutcomeValues = useMemo(
    () =>
      callOutcomeOptions?.filter((o) =>
        callOutcomeKeys?.includes(Number(o?.id)),
      ),
    [callOutcomeOptions, callOutcomeKeys],
  );

  const setSourceFilter = useCallback((selected: { id: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      source: selected?.map((s) => s?.id),
    }));
  }, []);

  const setCallOutcomesFilter = useCallback((selected: { id: string }[]) => {
    const ids = (selected ?? [])
      .map((s) => Number(s?.id))
      .filter((n) => !Number.isNaN(n));
    setFilters((prev) => ({ ...prev, callOutcome: ids }));
  }, []);

  const handleApply = useCallback(() => {
    const appliedFilters = hideSourceAndOutcome
      ? { ...filters, source: [], callOutcome: [] }
      : filters;
    onApply(appliedFilters);
    onClose();
  }, [filters, hideSourceAndOutcome, onApply, onClose]);

  const handleClear = useCallback(() => {
    const cleared = {
      callerHost: [],
      source: [],
      callOutcome: [],
      callDateStart: '',
      callDateEnd: '',
    };
    setFilters(cleared);
    onClearAll();
  }, [onClearAll]);

  const handleCancel = useCallback(() => {
    setFilters(initialFilters);
    onClose();
  }, [initialFilters, onClose]);

  useEffect(() => {
    sendGetCallOutcomeListRequest();
    sendGetProspectFiltersSequencesListRequest();
    sendGetTeamsAndMembersListRequest();
  }, [
    sendGetCallOutcomeListRequest,
    sendGetProspectFiltersSequencesListRequest,
    sendGetTeamsAndMembersListRequest,
  ]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      className="prospects-filters-modal call-logs-filters-modal"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title id="call-logs-filters-title">
          <span className="semibold-3">Filters</span>
        </Modal.Title>
        <Button className="close" onClick={onClose}>
          <Icon identifier="close" />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {(hasPermission(Permissions.TEAM_PHONE_CALL_READ) ||
          hasPermission(Permissions.ACCOUNT_PHONE_CALL_READ)) && (
          <ProspectsFilter
            label="Caller / Host"
            containerClassName="bs-mb-20"
            onSelect={setCallerHostFilter}
            options={callerHostOpts}
            labelKey="name"
            placeholder="Select User"
            values={callerHostValues}
            uniqueKey="id"
            isMultiSelect
            maxVisibleSelectedChips={2}
          />
        )}

        {!hideSourceAndOutcome && (
          <ProspectsFilter
            label="Source"
            containerClassName="bs-mb-20"
            onSelect={setSourceFilter}
            options={sourceOptions}
            labelKey="name"
            placeholder="Select Source"
            values={sourceValues}
            isMultiSelect
            maxVisibleSelectedChips={2}
          />
        )}

        {!hideSourceAndOutcome && (
          <ProspectsFilter
            label="Call Outcome"
            containerClassName="bs-mb-20"
            onSelect={setCallOutcomesFilter}
            options={callOutcomeOptions}
            labelKey="name"
            placeholder="Select Call Outcomes"
            values={callOutcomeValues}
            isMultiSelect
            maxVisibleSelectedChips={2}
          />
        )}

        <ProspectsFilter
          label="Call Date"
          containerClassName="bs-mb-20"
          onSelect={setCallDateFilter}
          options={[]}
          placeholder="Select Date Range"
          values={callDateValues}
          filterType={FilterType.Date}
          isDisabled={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex flex-row align-self-start flex-grow-1">
          <Button
            variant={Button.Variant.Light}
            theme={Button.Theme.Ghost}
            onClick={handleClear}
            className="rst-btn"
          >
            Clear All
          </Button>
          <div className="d-flex flex-row justify-content-end flex-grow-1 gap-16">
            <Button
              variant={Button.Variant.Outlined}
              theme={Button.Theme.Ghost}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button variant={Button.Variant.Primary} onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CallLogsFilters;
